import { useState, useEffect, useRef } from 'react';

export default function Web3Section() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: 'Metaverse Strategy Development',
      description: 'Crafting comprehensive strategies for your brand\'s presence in virtual worlds, including platform selection, experience design, and community building.',
      icon: 'ri-earth-line'
    },
    {
      title: 'NFT Minting & Strategy',
      description: 'Creating unique digital assets and developing strategic NFT campaigns that build community, generate revenue, and enhance brand value.',
      icon: 'ri-nft-line'
    },
    {
      title: 'Blockchain & Decentralized Applications Consultation',
      description: 'Guiding your business through blockchain integration, smart contract development, and decentralized application architecture.',
      icon: 'ri-links-line'
    },
    {
      title: 'Future Trends Analysis',
      description: 'Staying ahead of emerging technologies and digital trends to position your brand at the forefront of innovation and market evolution.',
      icon: 'ri-compass-3-line'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="web3"
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mr-4">
                <i className="ri-global-line text-2xl text-white"></i>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                Guiding Your Brand Into 
                <span className="bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent"> The Next Era</span>
              </h2>
            </div>
            
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              Navigate the future of digital with our Web3 and innovation consultation services. 
              From metaverse strategies to blockchain integration, we help you understand and 
              leverage emerging technologies to create new revenue streams, engage audiences 
              in novel ways, and position your brand as a forward-thinking industry leader.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button className="group bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                <span className="flex items-center justify-center gap-2">
                  Explore Web3 Solutions
                  <i className="ri-rocket-line group-hover:translate-y-1 transition-transform duration-300"></i>
                </span>
              </button>
              
              <a 
                href="/services/web3"
                className="group border-2 border-cyan-500 hover:bg-cyan-500 text-cyan-500 hover:text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
              >
                Learn More About Web3
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
              </a>
            </div>
          </div>

          {/* Services List */}
          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="space-y-6">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-500 hover:transform hover:scale-105 cursor-pointer"
                  onMouseEnter={() => setHoveredService(index)}
                  onMouseLeave={() => setHoveredService(null)}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center group-hover:animate-pulse flex-shrink-0">
                      <i className={`${service.icon} text-xl text-white`}></i>
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-300 group-hover:text-white transition-colors duration-300 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                      <i className="ri-arrow-right-line text-cyan-400 text-xl"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}